import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Swords, Rabbit, Sparkles, Trophy } from 'lucide-react';

const Home = () => {
  const navigate = useNavigate();

  const games = [
    {
      id: 'combat',
      title: 'COMBAT CARDIO',
      description: 'Punch targets in rhythm',
      icon: Swords,
      color: 'from-secondary/20 to-secondary/5',
      borderColor: 'border-secondary/50',
      shadowColor: 'shadow-neon-pink',
      route: '/game/combat'
    },
    {
      id: 'jump',
      title: 'JUMP MASTER',
      description: 'Jump to the beat',
      icon: Rabbit,
      color: 'from-primary/20 to-primary/5',
      borderColor: 'border-primary/50',
      shadowColor: 'shadow-neon-blue',
      route: '/game/jump'
    },
    {
      id: 'mirror',
      title: 'MIRROR WIPE',
      description: 'Wave to reveal the image',
      icon: Sparkles,
      color: 'from-accent/20 to-accent/5',
      borderColor: 'border-accent/50',
      shadowColor: 'shadow-neon-green',
      route: '/game/mirror'
    }
  ];

  return (
    <div className="min-h-screen w-full bg-black relative overflow-hidden">
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1531113165519-5eb0816d7e02?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxuZW9uJTIwY3liZXJwdW5rJTIwY2l0eSUyMGZpdG5lc3MlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3MDU0MDU5NHww&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen p-6">
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h1 className="font-heading text-6xl md:text-8xl font-black uppercase tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-primary via-secondary to-accent mb-4 animate-pulse-neon">
            RHYTHMFIT
          </h1>
          <p className="font-body text-lg md:text-xl text-muted-foreground tracking-wide uppercase">
            Turn Your Workout Into A Game
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-6xl mb-8">
          {games.map((game, index) => {
            const Icon = game.icon;
            return (
              <motion.div
                key={game.id}
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.2, duration: 0.6 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate(game.route)}
                className={`relative bg-gradient-to-br ${game.color} backdrop-blur-xl border ${game.borderColor} rounded-xl p-6 cursor-pointer group overflow-hidden ${game.shadowColor}`}
                data-testid={`game-card-${game.id}`}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                
                <div className="relative z-10">
                  <Icon className="w-12 h-12 mb-4 text-primary group-hover:text-accent transition-colors" />
                  <h3 className="font-heading text-2xl font-bold uppercase tracking-tight text-foreground mb-2">
                    {game.title}
                  </h3>
                  <p className="font-body text-muted-foreground text-sm uppercase tracking-wider">
                    {game.description}
                  </p>
                </div>

                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <span className="text-primary text-xl">→</span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.button
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/leaderboard')}
          className="flex items-center gap-3 bg-black/60 backdrop-blur-xl border border-accent/50 rounded-lg px-8 py-4 shadow-neon-green"
          data-testid="leaderboard-button"
        >
          <Trophy className="w-6 h-6 text-accent" />
          <span className="font-heading text-xl font-bold uppercase tracking-wider text-accent">
            LEADERBOARD
          </span>
        </motion.button>
      </div>
    </div>
  );
};

export default Home;
